/**
 * useWorkflowHumanTasks - Pendências humanas do Workflow 2.0 (Fase 6)
 *
 * Leitura por `get_workflow_human_tasks` e resolução por
 * `resolve_workflow_human_task` (aprovar / reprovar / concluir), ambas RPCs
 * seguras com validação de tenant e de responsável no banco.
 *
 * @version 1.2.0
 * RELEASE: 2026-10-06 v1.2.0 - Filtros opcionais (processo, vencidas, bloqueantes,
 *          resolvidas, paginação) para a página Pendências; também invalida
 *          'workflow-overview-kpis' ao resolver.
 * RELEASE: 2026-09-04 v1.1.0 - Ao resolver a pendência, invalida também
 *          'workflow-guard-check': a trava exibida nos botões de proposta e
 *          contrato passa a refletir a decisão na hora, sem F5.
 * RELEASE: 2026-08-30 v1.0.0 - Criação inicial (Fase 6)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

const logger = createLogger('useWorkflowHumanTasks');

/** Pendência exibida nas telas */
export interface WorkflowHumanTask {
  id: string;
  kind: 'aprovacao' | 'tarefa';
  title: string;
  description: string | null;
  status: string;
  blocking: boolean;
  due_at: string | null;
  sla_violated: boolean;
  assignee_user_id: string | null;
  assignee_name: string | null;
  /** v1.1.0: foto de perfil do responsável (profiles.avatar_url) */
  assignee_avatar_url?: string | null;
  task_id: string | null;
  instance_id: string;
  node_id: string;
  node_label: string | null;
  definition_name: string | null;
  root_entity_type: string;
  root_entity_id: string;
  resolution_note: string | null;
  resolved_at: string | null;
  /** v1.2.0: autor da decisão (aprovação/reprovação) — RPC v1.3.0 */
  resolved_by?: string | null;
  resolved_by_name?: string | null;
  resolved_by_avatar_url?: string | null;
  created_at: string;
}

interface Filtros {
  rootEntityType?: string | null;
  rootEntityId?: string | null;
  onlyMine?: boolean;
  includeResolved?: boolean;
  /** v1.2.0 (RPC v1.4.0): filtros opcionais da página Pendências */
  definitionId?: string | null;
  onlyOverdue?: boolean;
  onlyBlocking?: boolean;
  onlyResolved?: boolean;
  limit?: number;
  offset?: number;
}

export function useWorkflowHumanTasks(filtros: Filtros = {}) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  const queryClient = useQueryClient();

  const key = ['workflow-human-tasks', tenantId, filtros];

  const query = useQuery({
    queryKey: key,
    enabled: !!tenantId,
    staleTime: 15_000,
    queryFn: async (): Promise<WorkflowHumanTask[]> => {
      const { data, error } = await (supabase.rpc as any)('get_workflow_human_tasks', {
        p_root_entity_type: filtros.rootEntityType ?? null,
        p_root_entity_id: filtros.rootEntityId ?? null,
        p_only_mine: filtros.onlyMine ?? false,
        p_include_resolved: filtros.includeResolved ?? false,
        p_limit: filtros.limit ?? 50,
        p_definition_id: filtros.definitionId ?? null,
        p_only_overdue: filtros.onlyOverdue ?? false,
        p_only_blocking: filtros.onlyBlocking ?? false,
        p_only_resolved: filtros.onlyResolved ?? false,
        p_offset: filtros.offset ?? 0,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao carregar pendências do workflow', { error: error.message });
        throw error;
      }
      return (data ?? []) as WorkflowHumanTask[];
    },
  });

  /** Aprova, reprova ou conclui a pendência */
  const resolve = useMutation({
    mutationFn: async (input: { id: string; decision: 'aprovado' | 'reprovado' | 'concluido'; note?: string }) => {
      const { data, error } = await (supabase.rpc as any)('resolve_workflow_human_task', {
        p_human_task_id: input.id,
        p_decision: input.decision,
        p_note: input.note ?? null,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({ queryKey: ['workflow-human-tasks', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['deal-workflow'] });
      // v1.1.0: ao aprovar/reprovar, a trava exibida nos botões de proposta e
      // contrato muda na hora — sem esperar o próximo evento de tempo real.
      queryClient.invalidateQueries({ queryKey: ['workflow-guard-check'] });
      // v1.2.0: contadores do topo da página Processos
      queryClient.invalidateQueries({ queryKey: ['workflow-overview-kpis'] });
      toast.success(
        input.decision === 'aprovado' ? 'Pendência aprovada' :
        input.decision === 'reprovado' ? 'Pendência reprovada' : 'Pendência concluída',
      );
    },
    onError: (error: any) => {
      logger.error('Erro ao resolver pendência do workflow', { error: error?.message });
      toast.error(error?.message || 'Não foi possível resolver a pendência');
    },
  });

  return { tasks: query.data ?? [], isLoading: query.isLoading, refetch: query.refetch, resolve };
}
